import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useWidgets } from '@/context/WidgetContext';
import { useMsal } from '@azure/msal-react';
import { BarChart3, ChevronDown, DollarSign, LogOut, Settings, User } from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CurrentUserContext } from '../context/CurrentUserContext';
import { useSuggestions } from '../context/SuggestionsContext';
import { getMySiteBadges } from '../services/mysite/MySiteHome';
import { signOut } from '../services/auth/silent-sso';
import AppLauncherModal from './applauncher/AppLauncherModal';
import BadgesMenu from './badges/BadgesMenu';
import { PbsIcon } from './common/icons';
import { IMySettings } from './settings/IMySettings';
import { SettingsPanel } from './settings/SettingsPanel';
import SuggestionMenu from './suggestions/SuggestionMenu';
import SuggestionViewMorePanel from './suggestions/SuggestionViewMorePanel';
import { getProfilePicture } from '@/services/user/User';
import { useApiClient } from '@/context/ApiContext';
import { getDisplayName } from '../utils/userUtil';

interface IHeaderProps {
  onSettingsChanged: (settings: IMySettings) => void;
  settings: IMySettings;
}

interface IBadge {
  title: string;
  icon: string | null;
  description: string | null;
}
const Header = (props: IHeaderProps) => {
  const [isAppLauncherVisible, setAppLauncherVisible] = useState<boolean>(false);
  const [badges, setBadges] = useState<IBadge[]>([]);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [isViewMorePanelOpen, setIsViewMorePanelOpen] = useState<boolean>(false);
  const [canImpersonate, setCanImpersonate] = useState<boolean>(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);

  const { instance } = useMsal();
  const apiClient = useApiClient();
  const { widgets, setOpenWidget } = useWidgets();
  const { currentUser, isOnBehalfOf, profileProperties, accessControl, isLoading } = useContext(CurrentUserContext);
  const { refreshSuggestions } = useSuggestions();

  const hasCompletedTutorial = localStorage.getItem('pbsTutorialCompleted');

  // True once the badges call has settled (success or failure) — gates the badge
  // and suggestion menus so a badges outage never hides suggestions.
  const [headerReady, setHeaderReady] = useState<boolean>(false);
  const initials = (() => {
    const parts = (currentUser?.FullName || "").trim().split(/\s+/);
    const letters = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
    if (letters.length === 0 && currentUser?.Email)
      return currentUser?.Email[0]?.toUpperCase() || "U";
    return (letters[0] || "U") + (letters[1] || "");
  })();
  useEffect(() => {
    if (!isLoading && currentUser && currentUser.Id !== 0) {
      getMySiteBadges(currentUser.LoginName, apiClient)
        .then((response) => setBadges(response.data ?? []))
        .catch((err) => {
          console.error('Error fetching badges:', err);
          setBadges([]);
        })
        .finally(() => setHeaderReady(true));
      refreshSuggestions();
    }
  }, [currentUser, isLoading, apiClient, refreshSuggestions]);

  useEffect(() => {
    setCanImpersonate(accessControl.canRead('impersonation'))
  }, [accessControl])

  useEffect(() => {
    if (isOnBehalfOf) setProfilePic(null);
    else {
      getProfilePicture(apiClient)
        .then((res) => {
          let data : Blob = res.data;
          setProfilePic(URL.createObjectURL(data))
        })
        .catch((err) => {
          console.error(err);
          setProfilePic(null);
        })
    }
  }, [currentUser, apiClient, isOnBehalfOf])

  const onSettingsClicked = useCallback(() => setShowSettings(true), []);
  const onSettingsClosed = useCallback(() => setShowSettings(false), []);
  const onSettingsSaved = useCallback((args: any) => {
    props.onSettingsChanged(args);
    setShowSettings(false);
  }, [props]);

  return (
    <>
      <header className="bg-[#00345d] font-figtree text-white px-4 flex items-center h-[68px] relative z-50">
        {/* Logo */}
        <div className="shrink-0 flex items-center gap-3 group relative cursor-default">
          <img src={process.env.PUBLIC_URL + '/logo.svg'} alt="Team PBS" onClick={() => window.location.href = '/'} className="h-[35px] w-auto cursor-pointer" />
          {/* Mission statement tooltip */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-white text-[#00345d] text-sm rounded-lg shadow-xl p-4 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 z-[1002]">
            <p className="font-semibold mb-1 text-[#00345d]">Our Mission</p>
            <ul className="text-gray-600 leading-relaxed list-disc list-inside space-y-1">
              <li>
                Create a responsive system that applies the principles of PBS to meet the needs of individuals, families and organizations we serve to produce quality of life changes that last
              </li>
              <li>
                Develop an organization that attracts the most competent, enthusiastic people and turns them into proficient professionals
              </li>
            </ul>

          </div>
        </div>
        {/* Greeting — desktop only */}
        <span className="hidden md:flex ml-6 text-sm text-white/70 mt-1">
          Connecting Every Step of Care
        </span>

        {/* Widgets dropdown — desktop only, right-aligned */}
        <div className="hidden md:flex flex-1 justify-end ">
          <DropdownMenu >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 px-4 py-2 rounded-full text-base font-semibold transition-all duration-200 border-none cursor-pointer bg-primary text-white hover:bg-primary/80 widget-bar"
              >
                My Tools
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-48 z-[1001]">
              {widgets.map((widget) => (
                <DropdownMenuItem
                  key={widget.title}
                  className="cursor-pointer"
                  onClick={() => setOpenWidget(widget)}
                >
                  <span className="mr-2 opacity-70">
                    <PbsIcon iconName={widget.icon} size={18} />
                  </span>
                  {widget.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right Section */}
        <div className="flex items-center">
          {/* Badges */}
          {headerReady && badges.length > 0 && <BadgesMenu badges={badges} />}

          {/* Notification bell / counter */}
          {headerReady && <SuggestionMenu onViewMore={() => setIsViewMorePanelOpen(true)} />}

          {/* Restart tour — desktop only */}
          {hasCompletedTutorial && (
            <button
              className="hidden md:block text-sm text-white hover:bg-white/10 px-3 h-[50px] transition-colors mr-4 rounded"
              onClick={() => {
                localStorage.removeItem('pbsTutorialCompleted');
                window.location.reload();
              }}
            >
              Restart tour
            </button>
          )}
        </div>
        {/* Settings + User */}
        <button
          // signOut clears the shared pbs_login_hint cookie before the MSAL logout,
          // so the next silent sign-in does not target the account that just left.
          onClick={() => signOut(instance, { postLogoutRedirectUri: window.location.origin + '/#/logout' })}
          className="ml-auto h-9 w-9 flex items-center justify-center rounded-full bg-yellow-500/20 hover:bg-yellow-500/30 transition-colors"
          title="Log Out"
          aria-label="Log Out"
          type="button"
        >
          <LogOut className="w-5 h-5 text-yellow-300" />
        </button>
        <div className="flex items-center">


          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm hover:bg-white/10 px-3 h-[50px] transition-colors whitespace-nowrap rounded">
                <div
                  className="h-9 w-9 rounded-full overflow-hidden bg-white/10 text-white grid place-items-center text-sm font-semibold mr-1 shrink-0"
                  aria-label={currentUser?.FullName}
                >
                  {profilePic ? (
                    <img src={profilePic} alt={currentUser?.FullName} className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <span className="text-sm leading-relaxed max-w-[150px] break-words line-clamp-2 hidden md:block">
                  {getDisplayName(currentUser, profileProperties)}
                </span>
                <ChevronDown className="h-3 w-3 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={'mt-2'} align="end">
              <DropdownMenuItem
                onClick={() => window.open('https://profile.teampbs.com', '_blank')}
              >
                <User className="mr-2 h-4 w-4" />
                View/Edit Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open('https://profile.teampbs.com/#/pay-stubs', '_blank')}
              >
                <DollarSign className="mr-2 h-4 w-4" />
                Payments
              </DropdownMenuItem>
              {accessControl.canRead('epr_metrics_admin') && (
                <DropdownMenuItem onClick={() => { window.location.hash = '#/epr-metrics-admin'; }}>
                  <BarChart3 className="mr-2 h-4 w-4" />
                  EPR &amp; Metrics
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onSettingsClicked}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header >

      <AppLauncherModal isOpen={isAppLauncherVisible} onDismiss={() => setAppLauncherVisible(false)} />

      {
        showSettings && (
          <SettingsPanel
            canImpersonate={canImpersonate}
            onSettingsClosed={onSettingsClosed}
            onSettingsSaved={onSettingsSaved}
            settings={props.settings}
          />
        )
      }

      <SuggestionViewMorePanel
        isOpen={isViewMorePanelOpen}
        onDismiss={() => setIsViewMorePanelOpen(false)}
      />
    </>
  );
};

export default Header;
